<script lang="ts">
import { GuidesSchema } from '$lib/schema-org';
import { FULL_RETIREMENT_AGE } from '$lib/constants';
import GuideFooter from '../guide-footer.svelte';
import InlineCTA from '../InlineCTA.svelte';

const title = 'Normal Retirement Age (NRA): What It Means for Your Benefits';
const description =
  'Learn about Normal Retirement Age (NRA), also called Full Retirement Age (FRA), how it varies by birth year, and why it matters for calculating your Social Security benefits.';
const publishDate = new Date('2026-01-17T00:00:00+00:00');
const updateDate = new Date('2026-09-17T00:00:00+00:00');

let schema: GuidesSchema = new GuidesSchema();
schema.url = 'https://ssa.tools/guides/nra';
schema.title = title;
schema.image = '/laptop-piggybank.jpg';
schema.datePublished = publishDate.toISOString();
schema.dateModified = updateDate.toISOString();
schema.description = description;
schema.imageAlt =
  'Laptop with piggybank representing Social Security retirement planning';
schema.tags = [
  'Normal Retirement Age',
  'Full Retirement Age',
  'NRA',
  'FRA',
  'Social Security',
  'Retirement Planning',
];

// Build a display-friendly table from the constants
interface AgeRow {
  birthYears: string;
  ageDisplay: string;
  delayedCredit: string;
}

const fraTable: AgeRow[] = FULL_RETIREMENT_AGE.filter(
  (row) => row.minYear >= 1938 && row.maxYear <= 1960
).map((row) => {
  const yearRange =
    row.minYear === row.maxYear
      ? `${row.minYear}`
      : `${row.minYear}–${row.maxYear}`;

  const ageDisplay =
    row.ageMonths === 0
      ? `${row.ageYears}`
      : `${row.ageYears} and ${row.ageMonths} months`;

  const delayedCredit = `${(row.delayedIncreaseAnnual * 100).toFixed(1)}%`;

  return { birthYears: yearRange, ageDisplay, delayedCredit };
});

// Add the 1960+ row
fraTable.push({
  birthYears: '1960 or later',
  ageDisplay: '67',
  delayedCredit: '8.0%',
});
</script>

<svelte:head>
  <title>{title}</title>
  <meta name="description" content={description} />
  {@html schema.render()}
  {@html schema.renderSocialMeta()}
</svelte:head>

<div class="guide-page">
  <h1>{title}</h1>
  <p class="postdate">Published: {publishDate.toLocaleDateString()}</p>

  <h2>What Is Normal Retirement Age?</h2>

  <p>
    Normal Retirement Age (NRA), also called Full Retirement Age (FRA), is the
    age at which you're entitled to receive 100% of your calculated Social
    Security benefit, known as your
    <a href="/guides/pia">Primary Insurance Amount (PIA)</a>.
  </p>

  <p>
    The Social Security Administration officially uses both terms. "Full
    Retirement Age" appears more frequently in public-facing materials, while
    "Normal Retirement Age" is common in technical documents and benefit
    calculations. This site primarily uses NRA because "full" can be misleading:
    you can actually receive <em>more</em> than your "full" benefit by delaying
    past NRA, up to 24–32% more at age 70 depending on your birth year. NRA is
    simply the reference point: the age where there's no reduction for early
    filing and no increase for delayed filing.
  </p>

  <h2>NRA by Birth Year</h2>

  <p>
    Your <a href="https://www.ssa.gov/oact/progdata/nra.html">Normal Retirement Age depends entirely on when you were born</a>. Congress
    gradually increased NRA from 65 to 67 through
    <a href="https://www.ssa.gov/history/1983amend.html">legislation passed in 1983</a>,
    affecting those born in 1938 and later.
  </p>

  <div class="table-container">
    <table class="fra-table">
      <thead>
        <tr>
          <th>Year of Birth</th>
          <th>Normal Retirement Age</th>
          <th>Delayed Credit per Year</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>1937 or earlier</td>
          <td>65</td>
          <td>6.5%</td>
        </tr>
        {#each fraTable as row}
          <tr>
            <td>{row.birthYears}</td>
            <td>{row.ageDisplay}</td>
            <td>{row.delayedCredit}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

  <p>
    For those <a href="https://www.ssa.gov/benefits/retirement/planner/1960.html">born in 1960 or later, NRA is 67</a>.
    This has been stable since 1983
    and there are no currently scheduled changes, though future legislation
    could adjust this.
  </p>

  <InlineCTA type="calculator" />

  <h2>Filing Before Normal Retirement Age</h2>

  <p>
    You can file for Social Security retirement benefits as early as
    <a href="https://www.ssa.gov/benefits/retirement/planner/agereduction.html">age 62</a>, but
    doing so permanently reduces your monthly benefit. The reduction is
    calculated based on how many months early you file:
  </p>

  <div class="formula-box">
    <h3>Early Filing Reduction Formula</h3>
    <p>
      For the first 36 months before NRA, your benefit is
      <a href="https://www.ssa.gov/oact/quickcalc/earlyretire.html">reduced by 5/9 of 1% per month</a>
      (6.67% per year). For each month beyond 36, the reduction is
      5/12 of 1% per month (5% per year).
    </p>
  </div>

  <h3>Example: Filing at 62 with NRA of 67</h3>

  <p>
    If your NRA is 67 and you file at exactly 62, you're
    <a href="https://www.ssa.gov/benefits/retirement/planner/agereduction.html">filing 60 months early</a>:
  </p>

  <div class="calculation-example">
    <table>
      <tbody>
        <tr>
          <td>First 36 months: 36 × (5/9 of 1%)</td>
          <td>= 20.0% reduction</td>
        </tr>
        <tr>
          <td>Next 24 months: 24 × (5/12 of 1%)</td>
          <td>= 10.0% reduction</td>
        </tr>
        <tr class="total-row">
          <td><strong>Total reduction:</strong></td>
          <td><strong>30.0%</strong></td>
        </tr>
      </tbody>
    </table>
    <p class="example-note">
      If your PIA is $2,000, filing at 62 gives you $1,400/month permanently.
    </p>
  </div>

  <p>
    This reduction is permanent. Your benefit will still receive annual
    <a href="/guides/inflation">cost-of-living adjustments (COLA)</a>, but the
    base amount remains reduced for life. How many years it takes for the
    full benefit to make up for the early checks is covered in the
    <a href="/guides/break-even-age">break-even age guide</a>.
  </p>

  <h2>Filing After Normal Retirement Age</h2>

  <p>
    If you delay filing past your NRA, you earn delayed retirement credits
    that increase your benefit. These credits accrue monthly until age 70,
    after which there's no additional benefit to waiting.
  </p>

  <div class="formula-box">
    <h3>Delayed Retirement Credits</h3>
    <p>
      For those born in 1943 or later, the credit is
      <a href="https://www.ssa.gov/oact/ProgData/ar_drc.html">8% per year (2/3 of 1% per month)</a> for
      each year you delay past NRA, up to age 70.
    </p>
  </div>

  <h3>Example: Delaying to 70 with NRA of 67</h3>

  <div class="calculation-example">
    <table>
      <tbody>
        <tr>
          <td>Delay of 3 years (36 months): 36 × (2/3 of 1%)</td>
          <td>= 24% increase</td>
        </tr>
        <tr class="total-row">
          <td><strong>Benefit at 70:</strong></td>
          <td><strong>124% of PIA</strong></td>
        </tr>
      </tbody>
    </table>
    <p class="example-note">
      If your PIA is $2,000, delaying to 70 gives you $2,480/month.
    </p>
  </div>

  <p>
    One quirk: if you're already receiving benefits and delay past NRA, your
    delayed credits are <a href="https://www.ssa.gov/benefits/retirement/planner/delayret.html">applied in January of the following year</a>,
    not immediately. See our guide on the
    <a href="/guides/delayed-january-bump">delayed January bump</a> for more
    details.
  </p>

  <h2>Why NRA Matters Beyond Personal Benefits</h2>

  <p>NRA also shows up in a few other places.</p>

  <h3>Spousal Benefits</h3>
  <p>
    <a href="/guides/spousal-benefit-filing-date">Spousal benefits</a> are also
    reduced if filed before your NRA. However, unlike personal benefits, spousal
    benefits do <em>not</em> increase if you delay past NRA. There are
    <a href="https://www.ssa.gov/benefits/retirement/planner/applying7.html">no delayed credits for spousal benefits</a>. The same rules apply to
    <a href="/guides/divorced-spouse">divorced spouse benefits</a>.
  </p>

  <h3>Survivor Benefits</h3>
  <p>
    <a href="/guides/survivor-benefits">Survivor benefits</a> have a different
    NRA schedule (typically 2 years earlier than retirement NRA for the same
    birth year). Survivor benefits can be filed
    <a href="https://www.ssa.gov/benefits/survivors/survivorchartred.html">as early as age 60</a> (or 50 if
    disabled), with corresponding reductions. Unlike your own benefit, survivor
    benefits do not increase past survivor NRA.
  </p>

  <h3>The Earnings Test</h3>
  <p>
    If you work while receiving benefits before NRA, your benefits may be
    temporarily reduced due to the <a href="/guides/earnings-test">earnings test</a>.
    In 2026, if you earn more than $24,480 while receiving benefits before NRA,
    <a href="https://www.ssa.gov/benefits/retirement/planner/whileworking.html">$1 is withheld for every $2</a> over the limit. This reduction ends once you
    reach NRA, and any withheld benefits are returned to you through higher
    monthly payments.
  </p>

  <h2>Common Misconceptions</h2>

  <p>
    One common mistake: assuming that filing at your "Full Retirement Age" gets
    you the biggest check. It doesn't. If you can afford to wait, delaying
    until 70 gives you a higher monthly benefit for life, which works out to
    more total money if you live past your early 80s. The "full" in Full
    Retirement Age just means no reduction and no increase; it's the neutral
    reference point.
  </p>

  <p>
    Another: assuming everyone's NRA is 65. That was true for people born
    before 1938, but NRA has been rising since then. For anyone born in 1960 or
    later, it's 67. Planning around age 65 can throw off your numbers by a fair
    amount.
  </p>

  <p>
    Finally, some people believe the early filing reduction goes away once you
    reach NRA. It doesn't. If you file early, the reduction is permanent; your
    benefit never catches up to what it would have been had you waited (though
    it still receives annual COLA adjustments).
  </p>

  <h2>Calculate Your Specific Situation</h2>

  <p>
    There's no single right filing age; it depends on your health, your other
    income, and your spouse's situation. Use the
    <a href="/calculator">ssa.tools calculator</a> to see exactly how different
    filing ages affect your benefits based on your actual earnings record.
  </p>

  <p>
    The calculator shows your NRA based on your birth date and displays how your
    benefit changes at every possible filing age from 62 to 70.
  </p>

  <h2>Related Guides</h2>

  <ul>
    <li>
      <a href="/guides/pia">Primary Insurance Amount (PIA)</a> – The benefit
      amount you receive at NRA
    </li>
    <li>
      <a href="/guides/maximum">Maximum Social Security Benefit</a> – What it
      takes to receive the highest possible benefit
    </li>
    <li>
      <a href="/guides/spousal-benefit-filing-date">Spousal Benefits and Filing Dates</a>
      – How NRA affects spousal benefit calculations
    </li>
    <li>
      <a href="/guides/divorced-spouse">Divorced Spouse Benefits</a> – Eligibility
      rules for benefits based on an ex-spouse's record
    </li>
    <li>
      <a href="/guides/delayed-january-bump">Delayed January Bump</a> – How
      delayed credits are applied after NRA
    </li>
    <li>
      <a href="/guides/earnings-test">Working While Receiving Benefits</a> – How
      the earnings test affects benefits before NRA
    </li>
  </ul>

  <h2>Additional Resources</h2>

  <ul>
    <li><a href="https://www.ssa.gov/oact/progdata/nra.html">Social Security Administration: Normal Retirement Age</a></li>
    <li><a href="https://www.ssa.gov/history/1983amend.html">Social Security Administration: Social Security Amendments of 1983</a></li>
    <li><a href="https://www.ssa.gov/benefits/retirement/planner/1960.html">Social Security Administration: Born in 1960 or Later</a></li>
    <li><a href="https://www.ssa.gov/benefits/retirement/planner/agereduction.html">Social Security Administration: Retirement Age and Benefit Reduction</a></li>
    <li><a href="https://www.ssa.gov/oact/quickcalc/earlyretire.html">Social Security Administration: Benefit Reduction for Early Retirement</a></li>
    <li><a href="https://www.ssa.gov/oact/ProgData/ar_drc.html">Social Security Administration: Early or Delayed Retirement</a></li>
    <li><a href="https://www.ssa.gov/benefits/retirement/planner/delayret.html">Social Security Administration: Delayed Retirement Credits</a></li>
    <li><a href="https://www.ssa.gov/benefits/retirement/planner/applying7.html">Social Security Administration: Family Benefits</a></li>
    <li><a href="https://www.ssa.gov/benefits/survivors/survivorchartred.html">Social Security Administration: What You Could Get from Survivor Benefits</a></li>
    <li><a href="https://www.ssa.gov/benefits/retirement/planner/whileworking.html">Social Security Administration: Receiving Benefits While Working</a></li>
  </ul>

  <GuideFooter />
</div>

<style>
  .table-container {
    overflow-x: auto;
  }

  .fra-table {
    width: fit-content;
    max-width: 80%;
    border-collapse: collapse;
    margin: 20px auto;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .fra-table th,
  .fra-table td {
    padding: 10px 25px;
    text-align: left;
    border-bottom: 1px solid #e9ecef;
  }

  .fra-table th:first-child,
  .fra-table td:first-child {
    text-align: right;
  }

  .fra-table th {
    background-color: #e9ecef;
    font-weight: 600;
  }

  .fra-table tbody tr:hover {
    background-color: #f5f5f5;
  }

  .formula-box {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }

  .formula-box h3 {
    margin-top: 0;
    color: #2c3e50;
  }

  .formula-box p {
    margin-bottom: 0;
  }

  .calculation-example {
    background-color: #fff9e6;
    border: 1px solid #ffc107;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }

  .calculation-example table {
    width: 100%;
    border-collapse: collapse;
  }

  .calculation-example td {
    padding: 8px 12px;
    border-bottom: 1px solid #e9ecef;
  }

  .calculation-example .total-row {
    border-top: 2px solid #2c3e50;
    font-weight: bold;
  }

  .calculation-example .total-row td {
    border-bottom: none;
    padding-top: 12px;
  }

  .example-note {
    margin-top: 15px;
    margin-bottom: 0;
    font-style: italic;
    color: #555;
  }

  @media (max-width: 600px) {
    .fra-table {
      font-size: 14px;
    }

    .fra-table th,
    .fra-table td {
      padding: 8px 10px;
    }

    .calculation-example table {
      font-size: 14px;
    }

    .calculation-example td {
      padding: 6px 8px;
    }
  }
</style>
